/**
 * Coordinator Initialization
 *
 * Initializes all coordinators and registers their message handlers with the MessageRouter.
 * This is the single entry point for the new coordinator-based architecture.
 *
 * Called at app startup when USE_MESSAGE_ROUTER feature flag is enabled.
 */

import { PavilionCoordinator } from './pavilion/PavilionCoordinator';
import { NonsoleSanitizationMonitor } from './pavilion/NonsoleSanitizationMonitor';
import { GameLaunchCoordinator } from './game-launch/GameLaunchCoordinator';
import { SessionCoordinator } from './session/SessionCoordinator';
import { PeerCoordinator } from './peer/PeerCoordinator';
import { GameInstancePeerManager } from './game-instance/managers/GameInstancePeerManager';
import { MediaCoordinator } from './media/MediaCoordinator';
import { StatefulMacroCoordinator } from './macro/StatefulMacroCoordinator';
import { ScheduledScreenshotWatcher } from './scheduled-watcher/ScheduledScreenshotWatcher';
import { PeerLiteConnectionFactory } from '@playcastdotio/PeerCore';
import { AppCoordinator } from './app/AppCoordinator';
import { realtimeMessageRouter, playjectorMessageRouter } from '../services/MessageRouter';
import { replicationRouter } from './ReplicationRouter';
import {
  authState,
  pauseInputState,
  machineIdState,
  loginProfileState,
  driverState,
  sanitizationState,
  displayState,
  hostCampaignConfigState,
  hostState,
} from '../api/stores/shared'; // TODO-STRANGLER-FIG: Temporary legacy adapter
import { DisplaySourceManager } from './media/managers/DisplaySourceManager';
import type { RestartManager } from './restart/RestartManager';
import { initRestartManager } from './restart/restartInit';
import { AudioManager } from './media/managers/AudioManager';
import { DisplayOutputManager } from './media/managers/DisplayOutputManager';
import { StreamCoordinator } from './stream/StreamCoordinator';
import { StreamParticipantManager } from './stream/managers/StreamParticipantManager';
import { serviceContainer } from '../services/ServiceContainer';
import { RealtimeSignalingService } from '../services/peer/RealtimeSignalingService';
import { getStateStore } from '../api/stores';
import { getElectronStores } from '../api/stores/electronStores';
import { generators, websocket, HostMessageType } from '@playcastdotio/Messaging';
import { InputManager } from './peer/managers/InputManager';
import { CampaignInputManager } from './peer/managers/CampaignInputManager';
import { GamepadManager } from './peer/managers/GamepadManager';
import { ActivityTracker } from './peer/managers/ActivityTracker';
import { AdminPeerHandler } from './peer/handlers/AdminPeerHandler';
import { RecordingControlManager } from '../services/playjector/managers/RecordingControlManager';
import { BroadcastStatusIpcSyncManager } from './session/managers/BroadcastStatusIpcSyncManager';
import { ConnectedUsersIpcSyncManager } from './session/managers/ConnectedUsersIpcSyncManager';
import { createInputConfigStores } from './peer/stores/inputConfigStores';
import {
  USE_MESSAGE_ROUTER,
  USE_PAVILION_COORDINATOR,
  USE_SESSION_COORDINATOR,
  USE_PLAYJECTOR_COORDINATOR,
  USE_MEDIA_COORDINATOR,
  USE_BEACON_COORDINATOR,
  USE_PROVISIONING_COORDINATOR,
  USE_STREAM_ARCHITECTURE,
} from '../config/featureFlags';
import { BeaconCoordinator } from './beacon/BeaconCoordinator';
import { ProvisioningCoordinator } from './provisioning/ProvisioningCoordinator';
import { NonsoleExitCoordinator } from './nonsole/NonsoleExitCoordinator';
import { wireScheduledWatcherEvents } from './scheduled-watcher/watcherEventWiring';
import { GamingScoreCoordinator } from './gamingScore/GamingScoreCoordinator';
import { InstanceCapabilitiesProvider } from '../services/playjector/InstanceCapabilitiesProvider';
import { InstanceRecoveryManager } from './game-instance/managers/InstanceRecoveryManager';
import { createInstanceClosedHandler } from '../api/messaging/handlers/instanceClosed';
import { createCompositeInstanceLaunchedHandler } from '../api/messaging/handlers/instanceLaunched';
import { createAdminRestartInstanceHandler } from '../api/messaging/handlers/adminRestartInstance';
import constants from '@playcastdotio/GlobalConfig';
import type { GamingScorePersistedData } from '@playcastdotio/Messaging';
import { setLegacyMatchmakerShadowcamHealthSource } from '../api/modules/managers/matchmaker';
import { BASE_STREAM_CAMERA } from './media/cameraNames';

// Create coordinator singletons here (lazy, avoids GlobalConfig in tests)
// appCoordinator must be first — GameLaunchCoordinator injects it into GameLifecycleManager (avoids init↔game-launch cycle)
export const appCoordinator = new AppCoordinator(serviceContainer.analyticsManager);
export const pavilionCoordinator = new PavilionCoordinator(
  serviceContainer.realtimeService,
  serviceContainer.campaignConfigService,
  serviceContainer.analyticsManager
);
export const gameLaunchCoordinator = new GameLaunchCoordinator(
  serviceContainer.playjectorService,
  appCoordinator,
  serviceContainer.analyticsManager,
  serviceContainer.realtimeService,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.gameSaveManager : undefined,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.gameSaveConfigurationAvailability : undefined,
  USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR ? serviceContainer.campaignGameSaveConfiguration : undefined,
);
export const mediaCoordinator = new MediaCoordinator(() => driverState.currentState);
export const realtimeSignalingService = new RealtimeSignalingService();
export const peerCoordinator = new PeerCoordinator(new PeerLiteConnectionFactory());
export const gameInstancePeerManager = new GameInstancePeerManager(
  gameLaunchCoordinator.getGameInstanceStore(),
  peerCoordinator,
  serviceContainer.realtimeService,
);
export const sessionCoordinator = new SessionCoordinator(
  serviceContainer.realtimeService,
  serviceContainer.peerManager,
  serviceContainer.analyticsManager
);

// StatefulMacroCoordinator (singleton — owns retryCount across stopGame cycles)
// Phase 3 of docs/feature-plans/stateful-macro-system. Wired in
// initializeCoordinators after gameLaunchCoordinator.init() makes its
// MacroManager available, since the macro coordinator depends on it.
export let statefulMacroCoordinator: StatefulMacroCoordinator | null = null;

// ScheduledScreenshotWatcher (singleton — periodic idle-screen health check)
export let scheduledScreenshotWatcher: ScheduledScreenshotWatcher | null = null;
async function fetchGamingScore(machineId: string): Promise<GamingScorePersistedData | null> {
  const res = await fetch(
    `${constants.REALTIME_API_BASE_URL}realtime/machines/${encodeURIComponent(machineId)}/gaming-score`,
  );

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new Error(`Failed to fetch gaming score (${res.status})`);
  }

  return (await res.json()) as GamingScorePersistedData;
}

export const gamingScoreCoordinator = new GamingScoreCoordinator(
  serviceContainer.realtimeService,
  () => machineIdState.currentState || null,
  fetchGamingScore,
);

// Initialized lazily inside initializeCoordinators when USE_MEDIA_COORDINATOR is true
export let adminPeerHandler: AdminPeerHandler | null = null;

// Beacon coordinator singleton (conditional on USE_BEACON_COORDINATOR)
export const beaconCoordinator: BeaconCoordinator | null = USE_BEACON_COORDINATOR
  ? new BeaconCoordinator({
      realtimeService: serviceContainer.realtimeService,
      getMachineId: () => machineIdState.currentState,
    })
  : null;

// Provisioning coordinator singleton (conditional on USE_PROVISIONING_COORDINATOR)
export const provisioningCoordinator: ProvisioningCoordinator | null = USE_PROVISIONING_COORDINATOR
  ? new ProvisioningCoordinator({
      ipcService: serviceContainer.ipcService,
      realtimeService: serviceContainer.realtimeService,
      getMachineId: () => machineIdState.currentState,
      getHostId: () => appCoordinator.getHostAuthId(),
    })
  : null;

export const nonsoleExitCoordinator = new NonsoleExitCoordinator({
  ipcService: serviceContainer.ipcService,
  getHostId: () => appCoordinator.getHostAuthId(),
});

// Android multi-instance host Phase 2: machine capability provider. Queries
// Playjector's processCapabilities at startup and re-queries on every
// `notifyPlayjectorInitialized` IPC message (the existing Playjector
// reconnect signal — see api/messaging/handlers/ipc.ts). The last good reply
// feeds the v2 host state report machine section; Phase 3 threads it into the
// launch clamp.
export const instanceCapabilitiesProvider = new InstanceCapabilitiesProvider(
  serviceContainer.playjectorService,
);

// Android multi-instance host Phase 4: restart policy for crashed instances.
// Reacts to store transitions (failed → schedule a budgeted restart; ready →
// reset the budget; gone/stopping → cancel) and owns the per-instance
// lifecycle analytics events. Attached and wired in initializeCoordinators.
export const instanceRecoveryManager = new InstanceRecoveryManager({
  store: gameLaunchCoordinator.getGameInstanceStore(),
  restartInstance: (streamId, gameInstanceIndex) =>
    serviceContainer.playjectorService.restartInstance(streamId, gameInstanceIndex),
  getActiveStreamId: () =>
    gameLaunchCoordinator.getGameInstanceLaunchManager().getActiveAndroidStreamId(),
  analytics: serviceContainer.analyticsManager,
});

// Production-readiness Phase 5 (R5 host half): production binding for the
// MATCHMAKING_V2_ADMIN_RESTART_INSTANCE handler. Kept here (append-only) so
// the handler file stays free of the coordinator graph and matchmakingV2.ts
// only routes. Mirrors the dev-controls restart path: validate the index
// against the live store + active android stream, mark the restart
// intentional, then issue it — the recovery policy settles the outcome via
// the relaunch echo.
export const handleAdminRestartInstanceMessage = createAdminRestartInstanceHandler({
  getActiveStreamId: () =>
    gameLaunchCoordinator.getGameInstanceLaunchManager().getActiveAndroidStreamId(),
  hasLiveInstance: (gameInstanceIndex) =>
    gameLaunchCoordinator.getGameInstanceStore().getSessionByIndex(gameInstanceIndex) !== undefined,
  requestIntentionalRestart: (gameInstanceIndex) =>
    instanceRecoveryManager.requestIntentionalRestart(gameInstanceIndex),
});

// Stream tracking coordinator singleton. Owns the host's view of Playjector's stream world
// in-memory (replacing the former electron StreamRegistry + replicated streamState store). It is
// constructed at module scope so it is available in every mode, including prime, and initialized
// via initStreamCoordinator() below independently of USE_MESSAGE_ROUTER.
export const streamCoordinator = new StreamCoordinator({
  ipcService: serviceContainer.ipcService,
  // Android multi-instance host Phase 5: the desktop-hosting camera follows the
  // shared base-camera constant (coordinated C++ rename ShadowCam03 → ShadowCam00).
  desktopHostingCamera: BASE_STREAM_CAMERA,
  subscribePreviewCamera: (onCamera) => {
    const store = getStateStore('ipc').hostRuleState;
    const id = store.addListener((s) => onCamera(s.videoPreviewCamera));
    return () => store.removeListener(id);
  },
  getSelectedDisplayId: () => displayState.currentState.info.selectedDisplayId,
});

/**
 * Initialize the StreamCoordinator. Gated on USE_STREAM_ARCHITECTURE but intentionally decoupled
 * from USE_MESSAGE_ROUTER (which is false in prime), so prime mode keeps stream tracking. Safe to
 * call more than once — init() is idempotent. Called from App startup in all modes.
 */
export function initStreamCoordinator(): void {
  if (!USE_STREAM_ARCHITECTURE) {
    console.log('[Coordinators] 🚧 StreamCoordinator skipped (USE_STREAM_ARCHITECTURE disabled)');
    return;
  }
  streamCoordinator.init();
  console.log('[Coordinators] ✓ StreamCoordinator initialized');
}

// Beacon manager singletons (conditional on USE_BEACON_COORDINATOR)
export let displaySourceManager: DisplaySourceManager | null = null;
export let audioManager: AudioManager | null = null;
export let displayOutputManager: DisplayOutputManager | null = null;

// Restart manager singleton (always active)
export let restartManager: RestartManager = null!;

/**
 * Initialize all coordinators
 *
 * Call this once at app startup (before any messages are processed).
 */
export async function initializeCoordinators(): Promise<void> {
  if (!USE_MESSAGE_ROUTER) {
    console.log('[Coordinators] Feature flag disabled, skipping initialization');
    return;
  }

  console.log('[Coordinators] Initializing coordinator-based architecture...');
  // Android multi-instance host Phase 5 (rename risk note): print the expected
  // base stream camera at startup for field diagnosis — it MUST match
  // Playjector's Constants::BASE_STREAM_CAMERA in the same release.
  console.log(`[Coordinators] Expected base stream camera: ${BASE_STREAM_CAMERA} (must match Playjector Constants::BASE_STREAM_CAMERA)`);

  try {
    // Initialize AppCoordinator FIRST (calculates static host properties)
    // All other coordinators will read from this single source of truth
    await appCoordinator.init();
    const legacyStoreForGamepads = getStateStore('ipc');
    appCoordinator.registerHostGamepadStore(legacyStoreForGamepads.hostGamepadState);
    console.log('[Coordinators] ✓ AppCoordinator initialized');

    // Initialize ProvisioningCoordinator (pavilion-only, independent of other coordinators)
    if (USE_PROVISIONING_COORDINATOR && provisioningCoordinator) {
      provisioningCoordinator.init();
      provisioningCoordinator.registerMessageHandlers(realtimeMessageRouter);
      provisioningCoordinator.registerPlayjectorHandlers(playjectorMessageRouter);
      console.log('[Coordinators] ✓ ProvisioningCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 ProvisioningCoordinator skipped (feature flag disabled)');
    }

    if (USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR) {
      serviceContainer.initializeGameSaveStorage(realtimeMessageRouter);
    }

    // Initialize GameLaunchCoordinator (if enabled)
    // This must happen before PavilionCoordinator init so PavilionCoordinator can listen to its events
    if (USE_PLAYJECTOR_COORDINATOR) {
      await gameLaunchCoordinator.init(pavilionCoordinator, sessionCoordinator);
      gameLaunchCoordinator.registerReplicationHandler(replicationRouter);
      console.log('[Coordinators] ✓ GameLaunchCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 GameLaunchCoordinator skipped (feature flag disabled)');
    }

    // Initialize SessionCoordinator (manages presence reporting)
    // Pass appCoordinator to read static properties
    // Pass gameLaunchCoordinator and peerCoordinator so it can listen to their events
    // Must be initialized BEFORE PeerCoordinator so PeerCoordinator can listen to streamStopped events
    if (USE_SESSION_COORDINATOR) {
      await sessionCoordinator.init(
        appCoordinator,
        USE_PLAYJECTOR_COORDINATOR ? gameLaunchCoordinator : undefined,
        peerCoordinator
      );
      console.log('[Coordinators] ✓ SessionCoordinator initialized');
      sessionCoordinator.events.joinableChanged.addListener(({ joinable }) => {
        hostState.updateState({
          ...hostState.currentState,
          streamJoinable: joinable,
        });
      });
    } else {
      console.log('[Coordinators] 🚧 SessionCoordinator skipped (feature flag disabled)');
    }

    gamingScoreCoordinator.init();
    console.log('[Coordinators] ✓ GamingScoreCoordinator initialized');

    if (USE_MEDIA_COORDINATOR) {
      await mediaCoordinator.init();

      mediaCoordinator.reportDriverStatus();
      driverState.addListener(() => mediaCoordinator.reportDriverStatus());
      gameLaunchCoordinator.events.instanceShadowcamsChanged.addListener(
        ({ shadowCamNames }) => {
          mediaCoordinator.setRequiredSessionCameras(shadowCamNames);
        },
      );

      // Android multi-instance host Phase 5: feed required session cameras from
      // store records, echo-preferred (resolveInstanceCamera inside
      // setRequiredInstanceCameras). instanceSessionsChanged also fires after
      // launch settles, so a differing echo corrects the required set.
      gameLaunchCoordinator.events.instanceSessionsChanged.addListener(
        ({ sessions }) => {
          mediaCoordinator.setRequiredInstanceCameras(sessions);
        },
      );

      // Wire shadowcam health into both matchmaker paths (USE_MESSAGE_ROUTER on/off).
      const shadowcamHealthSource = mediaCoordinator.getShadowcamPresenceMonitor();
      pavilionCoordinator.setShadowcamHealthSource(shadowcamHealthSource);
      setLegacyMatchmakerShadowcamHealthSource(shadowcamHealthSource);

      console.log('[Coordinators] ✓ MediaCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 MediaCoordinator skipped (feature flag disabled)');
    }

    // Live connected user/admin peer count -> PlayJector (for service affinity gating).
    // Declared at function scope so the auth bridge below can resync even though the
    // manager instance is created inside the USE_MEDIA_COORDINATOR block.
    let connectedUsersIpcSyncManager: ConnectedUsersIpcSyncManager | null = null;

    // Initialize PeerCoordinator (manages peer connections and player lifecycle)
    // Subscribes to MediaCoordinator.tracksChanged for track updates to peers
    // Gated by USE_MEDIA_COORDINATOR so peer/admin/input can be disabled independently of SessionCoordinator
    if (USE_MEDIA_COORDINATOR) {
      if (USE_PLAYJECTOR_COORDINATOR) {
        const launchManager = gameLaunchCoordinator.getGameInstanceLaunchManager();
        peerCoordinator.setGameInstanceSessionResolver({
          getSessionForInstance: (gameInstanceIndex) => {
            const session = gameLaunchCoordinator.getGameInstanceStore().getSessionByIndex(gameInstanceIndex);
            // Occupied stays routable: the remaining players of an active match
            // still join through the instance peer. Cooldown has no players left.
            if (!session || (session.status !== 'ready' && session.status !== 'occupied')) return null;
            return launchManager.toPeerSession(session);
          },
        });
      }
      const legacyIceState = getStateStore('ipc').iceServerState.currentState;
      const forceTurn = !legacyIceState.allowPeerConnections;

      const ipcService = serviceContainer.ipcService;

      /** Admin offer is handled before AdminPeerHandler runs; ShadowCam must be bound first or the track can stick on solid green. */
      const PLAYJECTOR_SETSOURCE_STAGGER_MS = 500;
      const SHADOWCAM_SOURCE_ACK_TIMEOUT_MS = 3_000;

      /**
       * Wait for the camera to be enumerable AND for the setSource bind to complete.
       * The camera appears in Chromium's device list before Playjector's 500ms setSource
       * stagger fires, so capturing immediately after enumeration yields the green placeholder.
       * We poll for enumeration, then apply the historical stagger delay to ensure bind.
       */
      const waitForShadowcamEnumerated = async (shadowcamName: string): Promise<void> => {
        const deadline = Date.now() + SHADOWCAM_SOURCE_ACK_TIMEOUT_MS;
        while (Date.now() < deadline) {
          await mediaCoordinator.getDeviceConfig().enumerateDevices();
          const found = mediaCoordinator
            .getDeviceConfig()
            .getShadowcams()
            .some(
              (cam) =>
                cam.displayName?.includes(shadowcamName) || cam.deviceId?.includes(shadowcamName),
            );
          if (found) {
            // Camera is enumerable but setSource (bind) fires 500ms after getSources reply.
            // Wait for that stagger plus a safety margin to avoid capturing the green placeholder.
            await new Promise<void>((resolve) => setTimeout(resolve, PLAYJECTOR_SETSOURCE_STAGGER_MS + 450));
            return;
          }
          await new Promise<void>((resolve) => setTimeout(resolve, 100));
        }
        console.warn(
          `[Coordinators] Shadowcam ${shadowcamName} not enumerated within ${SHADOWCAM_SOURCE_ACK_TIMEOUT_MS}ms — falling back to setSource stagger`,
        );
        await new Promise<void>((resolve) => setTimeout(resolve, PLAYJECTOR_SETSOURCE_STAGGER_MS + 450));
      };

      const beforeAdminMediaCapture = async (): Promise<void> => {
        if (USE_STREAM_ARCHITECTURE) {
          // Prime the stream coordinator (base stream already targets the primary output/sink).
          streamCoordinator.prime();
        } else {
          ipcService.send(generators.camera.getSources());
        }
        // Multi-cam: the always-on base stream camera is BASE_STREAM_CAMERA (ShadowCam00).
        await waitForShadowcamEnumerated(BASE_STREAM_CAMERA);
      };

      const activateShadowcam = async (shadowcamName: string): Promise<void> => {
        console.log(`[Coordinators] Activating shadowcam ${shadowcamName} via Playjector`);
        if (USE_STREAM_ARCHITECTURE) {
          // Stream architecture: activate the requested camera by binding it as a base-stream frame
          // sink (Playjector plugs the DShow device on bind), then wait for the device to enumerate.
          // waitForFrameSink event-drives off the async prime replies (the catalog is not populated
          // synchronously after prime). activateFrameSink is ADDITIVE + pinned, so activating a peer
          // camera (e.g. mobile) never removes a previously-activated peer or the hosting camera - a
          // later preview swap won't pause it. Keep the throw-on-timeout device gate so
          // ensureActivation cannot report success for a camera that never came up.
          streamCoordinator.prime();
          const sinkId = await streamCoordinator.waitForFrameSink(shadowcamName);
          if (sinkId !== undefined) {
            streamCoordinator.activateFrameSink(sinkId);
          }
          const ready = await mediaCoordinator.waitForShadowcamDevice(shadowcamName);
          if (!ready) {
            throw new Error(`Shadowcam ${shadowcamName} not enumerated within timeout`);
          }
        } else {
          // Main mirrors this toggle into the sourced camera set as it passes through, so the
          // getSources reply below binds a display to this camera rather than skipping it.
          ipcService.send(generators.camera.startStopToggle(shadowcamName, true));
          ipcService.send(generators.camera.getSources());
          const ready = await mediaCoordinator.waitForShadowcamDevice(shadowcamName);
          if (!ready) {
            throw new Error(`Shadowcam ${shadowcamName} not enumerated within timeout`);
          }
        }
      };

      const deactivateShadowcam = async (shadowcamName: string): Promise<void> => {
        if (USE_STREAM_ARCHITECTURE) {
          // Stream architecture binds cameras via frame sinks; legacy startStopToggle(false) can
          // tear down devices still referenced by the stream coordinator or session capture.
          console.log(`[Coordinators] Skipping legacy deactivate for ${shadowcamName} (stream architecture)`);
          return;
        }
        console.log(`[Coordinators] Deactivating on-demand shadowcam ${shadowcamName}`);
        ipcService.send(generators.camera.startStopToggle(shadowcamName, false));
      };

      // Re-assert AudioCaster as default render via hub.broadcastStatus →
      // SetBroadcastActive(true) → ReassertAudioCasterDefaultEndpoint. Guest teardown
      // may have restored host speakers; game-audio capture needs AudioCaster wired again.
      const ensureAudioCasterDefault = async (): Promise<void> => {
        const hostAuthId = appCoordinator.getHostAuthId();
        console.log('[AUDIO-BUG-12009:Coordinators] Ensuring AudioCaster default via broadcastStatus(streaming)');
        ipcService.send(generators.hub.broadcastStatus('streaming', hostAuthId));
        connectedUsersIpcSyncManager?.resync();
      };

      await peerCoordinator.init(sessionCoordinator, mediaCoordinator, realtimeSignalingService, {
        iceTransportPolicy: forceTurn ? 'relay' : 'all',
        beforeAdminMediaCapture,
        activateShadowcam,
        // Android multi-instance host Phase 5: while an android launch is
        // active, instance session cameras are activated by capture (streams
        // model) — PeerCoordinator skips the explicit activation above.
        getActiveAndroidStreamId: () =>
          gameLaunchCoordinator.getGameInstanceLaunchManager().getActiveAndroidStreamId(),
        deactivateShadowcam,
        ensureAudioCasterDefault,
      });

      // One HOST peer per ready game instance; reports carry the assigned ids.
      gameInstancePeerManager.attach((index) => {
        gameLaunchCoordinator.getMatchLifecycleManager().handleInstancePeerAssigned(index);
        gameLaunchCoordinator.getGameInstanceReportManager().flush();
      });
      gameLaunchCoordinator
        .getGameInstanceReportManager()
        .setInstancePeerResolver((index) => gameInstancePeerManager.getPeerIdForInstance(index));
      gameLaunchCoordinator
        .getMatchLifecycleManager()
        .setInstancePeerResolver((index) => gameInstancePeerManager.getPeerIdForInstance(index));
      // v2 answer identity: session-bound connections signal FROM their
      // instance peer — a v2 host never holds the v1 global host peer.
      peerCoordinator.setGameInstancePeerIdResolver((index) => gameInstancePeerManager.getPeerIdForInstance(index));
      // Occupancy truth (pool-match-lifecycle Phase 2): peer connect/disconnect
      // drives per-session playerCount and the ready→occupied→cooldown FSM.
      peerCoordinator.setGameInstanceOccupancyTracker(gameLaunchCoordinator.getMatchLifecycleManager());
      // Capacity truth (pool-match-lifecycle Phase 5, wired in the Phase 7
      // review follow-up): the authoritative gaming score feeds the hardware
      // cap side of hostEffectiveMax. Until a score arrives the documented
      // fallback applies (configured maxPlayers stands alone); the allocator
      // re-syncs before every mumu launch.
      gameLaunchCoordinator.setHardwareInfoProvider(() => gamingScoreCoordinator.getLatestScore());
      const inputStores = createInputConfigStores();

      // Bridge legacy stores -> coordinator-owned stores (strangler fig)
      const legacyStore = getStateStore('ipc');
      legacyStore.gamepadState.addListener((gs) => {
        inputStores.gamepadConfig.updateState({
          autoAssign: gs.autoAssign,
          maxAssignments: gs.maxAssignments,
          shareFlags: [gs.shareGamepadOne, gs.shareGamepadTwo, gs.shareGamepadThree, gs.shareGamepadFour],
        });
      });
      legacyStore.hostRuleState.addListener((rs) => {
        inputStores.inputRules.updateState({ enableGamepadFiltering: rs.enableGamepadFiltering });
      });
      pauseInputState.addListener((ps) => {
        inputStores.pauseInput.updateState(ps);
      });

      const gamepadManager = new GamepadManager({
        ipcService,
        gamepadConfigStore: inputStores.gamepadConfig,
        inputRulesStore: inputStores.inputRules,
        appCoordinator,
      });

      const activityTracker = new ActivityTracker({
        afkTimeoutMs: 10_000,
        onAfkChanged: (peerId, afk) => {
          peerCoordinator.events.afkStateChanged.emit({ peerId, afk });
        },
        onKick: (peerId) => {
          peerCoordinator.kickPeer(peerId, 'kicked:afk');
        },
      });

      const recordingControlManager = new RecordingControlManager({
        playjectorService: serviceContainer.playjectorService,
        sendIpc: (msg: unknown) => ipcService.send(msg as any),
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      });
      serviceContainer.setRecordingControlManager(recordingControlManager);

      adminPeerHandler = new AdminPeerHandler({
        ipcService,
        realtimeService: serviceContainer.realtimeService,
        appCoordinator,
        peerCoordinator,
        pavilionCoordinator,
        sessionPresenceUpdater: sessionCoordinator,
        recordingControlManager,
      });

      const inputManager = new InputManager({
        peerCoordinator,
        ipcService,
        pauseInputStore: inputStores.pauseInput,
        gamepadManager,
        adminPeerHandler,
        activityTracker,
        // Android multi-instance host Phase 5: late-bound android streamId
        // resolver — while an android launch is active, routed player input is
        // stamped { streamId, gameInstanceIndex } instead of legacy sessionId.
        getActiveAndroidStreamId: () =>
          gameLaunchCoordinator.getGameInstanceLaunchManager().getActiveAndroidStreamId(),
      });

      peerCoordinator.registerInputStores(inputStores);
      peerCoordinator.registerGamepadManager(gamepadManager);
      peerCoordinator.registerActivityTracker(activityTracker);
      peerCoordinator.getTypeRegistry().register('admin', adminPeerHandler);

      peerCoordinator.events.dataReceived.addListener(({ peerId, data }) => {
        inputManager.handleMessage(peerId, data);
      });

      peerCoordinator.events.onPlayerLeft.addListener(({ peerId }) => {
        gamepadManager.removePeer(peerId);
        activityTracker.removePeer(peerId);
      });
      peerCoordinator.events.onAdminLeft.addListener(({ peerId }) => {
        gamepadManager.removePeer(peerId);
      });

      // Legacy playjectorHostModeEvents.ts was the only sender of hub.broadcastStatus,
      // but it's gated off when USE_MESSAGE_ROUTER is true. Playjector relies on
      // that message to run its FlushKeyboard/FlushMouse/FlushTouches/FlushGamepads
      // path on broadcast-state transitions; without this bridge, stuck inputs
      // persist across stream stops in the coordinator flow.
      new BroadcastStatusIpcSyncManager({
        ipcService,
        sessionCoordinator,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      }).start();

      // Bridge live connected user/admin peer count -> PlayJector so the service can gate
      // CPU/GPU affinity on real activity. Deliberately NOT coupled to broadcast status.
      connectedUsersIpcSyncManager = new ConnectedUsersIpcSyncManager({
        ipcService,
        peerCoordinator,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      });
      connectedUsersIpcSyncManager.start();

      // Bridge machineIdState -> truncated machine ID replication to players (strangler fig)
      const electronStores = getElectronStores();
      let currentTruncMachineId = '';

      const broadcastTruncMachineId = () => {
        if (!currentTruncMachineId) return;
        console.log('[MachineId-Bridge] Broadcasting truncMachineId to all peers');
        const msg = generators.state.replication('truncMachineIdState', currentTruncMachineId);
        peerCoordinator.broadcastToAll(JSON.stringify(msg));
      };

      electronStores.machineIdState.addListener((machineId) => {
        currentTruncMachineId = machineId.substring(6, 20);
        broadcastTruncMachineId();
      });
      if (electronStores.machineIdState.currentState) {
        currentTruncMachineId = electronStores.machineIdState.currentState.substring(6, 20);
      }

      peerCoordinator.events.onPlayerJoined.addListener(({ peerId }) => {
        console.log(`[MachineId-Bridge] onPlayerJoined: ${peerId}, truncMachineId=${currentTruncMachineId || '(empty)'}`);
        if (!currentTruncMachineId) return;
        const msg = generators.state.replication('truncMachineIdState', currentTruncMachineId);
        const sent = peerCoordinator.sendToPeer(peerId, JSON.stringify(msg));
        console.log(`[MachineId-Bridge] sendToPeer result: ${sent}`);
      });

      // Forward player-joined events to PavilionCoordinator so it can apply
      // campaign-configured matchmaking cooldowns (e.g. Hitman 30-min contract clock).
      // The coordinator is idempotent within a launch instance, so multiple joins
      // for the same launch only start the timer once.
      peerCoordinator.events.onPlayerJoined.addListener(() => {
        pavilionCoordinator.notifyPlayerJoined();
      });

      // Gate player input to the active campaign's supported input methods
      // (HostCampaignConfig.supportedInputs). Clamps PeerPermissions on join and
      // whenever the campaign config loads/clears (GitHub #13807), and notifies
      // each player client of its granted access via setInputAccess.
      const campaignInputManager = new CampaignInputManager({
        peerCoordinator,
        pavilionCoordinator,
        getHostAuthId: () => authState.currentState.hostAuthId,
      });
      campaignInputManager.init();

      // Android multi-instance: grant Play on the instance stream so named-player
      // input (header.user.id = peerId) passes Playjector's RouteInstanceInput gate.
      const streamParticipantManager = new StreamParticipantManager({
        send: (message) => ipcService.send(message),
        getActiveAndroidStreamId: () =>
          gameLaunchCoordinator.getGameInstanceLaunchManager().getActiveAndroidStreamId(),
      });
      peerCoordinator.events.onPlayerJoined.addListener(({ peerId, metadata }) => {
        streamParticipantManager.attachPlayer(peerId, metadata.playjectorSession?.gameInstanceIndex);
      });
      peerCoordinator.events.onPlayerLeft.addListener(({ peerId }) => {
        streamParticipantManager.detachPlayer(peerId);
      });

      console.log('[Coordinators] ✓ PeerCoordinator initialized (with InputManager, GamepadManager, AdminPeerHandler)');
    } else {
      console.log('[Coordinators] 🚧 PeerCoordinator skipped (USE_MEDIA_COORDINATOR disabled)');
    }

    // Initialize PavilionCoordinator (MVP - fully functional)
    // Pass appCoordinator to read static properties when sending HOST_CONFIG
    // Pass gameLaunchCoordinator so it can listen to campaign switch events
    // Pass sessionCoordinator so it can orchestrate stream start on match found
    pavilionCoordinator.init(
      appCoordinator,
      USE_PLAYJECTOR_COORDINATOR ? gameLaunchCoordinator : undefined,
      USE_SESSION_COORDINATOR ? sessionCoordinator : undefined
    );
    pavilionCoordinator.registerMessageHandlers(realtimeMessageRouter);
    pavilionCoordinator.registerHostMessageHandlers(realtimeMessageRouter);
    nonsoleExitCoordinator.registerMessageHandlers(realtimeMessageRouter);
    console.log('[Coordinators] ✓ PavilionCoordinator initialized');

    const sanitizationMonitor = new NonsoleSanitizationMonitor(() => sanitizationState.currentState);
    pavilionCoordinator.setSanitizationHealthSource(sanitizationMonitor);
    sanitizationState.addListener(() => sanitizationMonitor.notifyIfChanged());

    // Initialize StatefulMacroCoordinator (Phase 3 of stateful-macro-system).
    // Requires GameLaunchCoordinator's MacroManager (created during its init()),
    // so we wire it up *after* both gameLaunch and pavilion are initialized.
    if (USE_PLAYJECTOR_COORDINATOR) {
      const macroManager = (gameLaunchCoordinator as unknown as { managers: { macro: import('./game-launch/managers/MacroManager').MacroManager } }).managers.macro;
      if (macroManager) {
        statefulMacroCoordinator = new StatefulMacroCoordinator({
          realtimeService: serviceContainer.realtimeService,
          playjectorService: serviceContainer.playjectorService,
          macroManager,
          gameLaunchCoordinator,
          pavilionCoordinator,
          // Production-readiness Phase 5 (R13 host): per-instance macro
          // capture targeting. "Live" mirrors the peer-eligibility set
          // (ready/occupied/cooldown) — instances with a monitor a capture
          // can run against; launching/failed/restarting instances are
          // skipped (their monitors are not converged or gone).
          getActiveAndroidStreamId: () =>
            gameLaunchCoordinator.getGameInstanceLaunchManager().getActiveAndroidStreamId(),
          getLiveGameInstanceIndices: () =>
            gameLaunchCoordinator.getGameInstanceStore().getSessions()
              .filter((s) => s.status === 'ready' || s.status === 'occupied' || s.status === 'cooldown')
              .map((s) => s.gameInstanceIndex),
        });
        statefulMacroCoordinator.init();
        statefulMacroCoordinator.registerMessageHandlers(realtimeMessageRouter);

        gameLaunchCoordinator.setCampaignSwitchSettleGuard(
          () => statefulMacroCoordinator?.isWaitingOutSettle() ?? false,
        );

        console.log('[Coordinators] ✓ StatefulMacroCoordinator initialized (settle guard wired)');
      } else {
        console.warn('[Coordinators] ⚠️  StatefulMacroCoordinator skipped — GameLaunchCoordinator has no MacroManager');
      }
    } else {
      console.log('[Coordinators] 🚧 StatefulMacroCoordinator skipped (USE_PLAYJECTOR_COORDINATOR disabled)');
    }

    // Initialize ScheduledScreenshotWatcher (periodic idle-screen health check).
    // Requires GameLaunchCoordinator (gameReady event), PavilionCoordinator
    // (campaign config), SessionCoordinator (stream lifecycle), and PlayjectorService.
    if (USE_PLAYJECTOR_COORDINATOR && USE_SESSION_COORDINATOR) {
      scheduledScreenshotWatcher = new ScheduledScreenshotWatcher({
        playjectorService: serviceContainer.playjectorService,
        gameLaunchCoordinator,
        pavilionCoordinator,
        sessionCoordinator,
      });
      scheduledScreenshotWatcher.init();
      wireScheduledWatcherEvents(scheduledScreenshotWatcher, pavilionCoordinator);
      console.log('[Coordinators] ✓ ScheduledScreenshotWatcher initialized');
    } else {
      console.log('[Coordinators] 🚧 ScheduledScreenshotWatcher skipped (feature flags disabled)');
    }

    // TODO-STRANGLER-FIG: Temporary adapter for authState until IdentityCoordinator is built
    // PavilionCoordinator and AppCoordinator need authId.
    // In the new architecture, IdentityCoordinator would own authState and emit events.
    authState.addListener((state) => {
      if (state.hostAuthId) {
        appCoordinator.updateHostAuthId(state.hostAuthId);
        pavilionCoordinator.updateAuthId(state.hostAuthId);
        // Re-send the current connected-user count once the host auth id is (re)bridged.
        // The initial report may have gone out with an empty/experimental id and then be
        // suppressed because the peer set hadn't changed; resync forces a fresh report with
        // the valid id.
        connectedUsersIpcSyncManager?.resync();
      }
    });
    if (authState.currentState.hostAuthId) {
      appCoordinator.updateHostAuthId(authState.currentState.hostAuthId);
      pavilionCoordinator.updateAuthId(authState.currentState.hostAuthId);
      // Resync after the initial auth-state snapshot is applied: the manager's own
      // start() already sent an initial report before this branch ran (with the empty or
      // experimental host id), so if the authenticated id is already present and the peer
      // count doesn't change, PlayJector wouldn't otherwise receive the authenticated count.
      connectedUsersIpcSyncManager?.resync();
    }

    // Initialize BeaconCoordinator (conditional)
    if (USE_BEACON_COORDINATOR && beaconCoordinator) {
      const beacon = beaconCoordinator;
      beacon.init(appCoordinator, sessionCoordinator, peerCoordinator);
      beacon.registerMessageHandlers(realtimeMessageRouter);

      // Bridge authState to BeaconCoordinator (same pattern as PavilionCoordinator)
      if (authState.currentState.hostAuthId) {
        beacon.updateAuthId(authState.currentState.hostAuthId);
      }
      authState.addListener((state) => {
        if (state.hostAuthId) {
          beacon.updateAuthId(state.hostAuthId);
        }
      });

      console.log('[Coordinators] ✓ BeaconCoordinator initialized');
    } else {
      console.log('[Coordinators] 🚧 BeaconCoordinator skipped (feature flag disabled)');
    }

    // Beacon and Pavilion share the same capture-source managers. Prime does not.
    if (USE_MEDIA_COORDINATOR && (USE_BEACON_COORDINATOR || USE_PAVILION_COORDINATOR)) {
      const legacyStores = getStateStore('ipc');
      displaySourceManager = new DisplaySourceManager({
        ipcService: serviceContainer.ipcService,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
        getActiveShadowcams: () => legacyStores.activeShadowcamState.currentState.active,
      });
      displaySourceManager.registerReplicationHandler(replicationRouter);

      const cameras = mediaCoordinator.getDeviceConfig().getCameras();
      if (cameras.length > 0) {
        displaySourceManager.onCamerasChanged(cameras);
      }
      mediaCoordinator.events.deviceListChanged.addListener(() => {
        displaySourceManager?.onCamerasChanged(mediaCoordinator.getDeviceConfig().getCameras());
      });

      audioManager = new AudioManager({
        ipcService: serviceContainer.ipcService,
        getHostAuthId: () => appCoordinator.getHostAuthId(),
      });
      audioManager.registerReplicationHandler(replicationRouter);

      const deviceConfig = mediaCoordinator.getDeviceConfig();
      audioManager.updateDeviceLists(
        deviceConfig.getDesktopAudioSources(),
        deviceConfig.getMicrophones(),
      );
      mediaCoordinator.events.deviceListChanged.addListener(() => {
        const dc = mediaCoordinator.getDeviceConfig();
        audioManager?.updateDeviceLists(
          dc.getDesktopAudioSources(),
          dc.getMicrophones(),
        );
      });

      // Desktop volume stays at unity on the published track. The beacon slider
      // is intentionally not bridged to mediaCoordinator.setVolume().
      displayOutputManager = new DisplayOutputManager({
        ipcService: serviceContainer.ipcService,
        getActiveShadowcams: () => legacyStores.activeShadowcamState.currentState.active,
        streamCoordinator,
      });
      displayOutputManager.registerReplicationHandler(replicationRouter);

      sessionCoordinator.events.statusChanged.addListener((e) => {
        const isLive = e.status === 'hosting';
        displayOutputManager?.updateStreamStatus(isLive, isLive ? Date.now() : 0);
      });

      console.log('[Coordinators] ✓ Capture source managers initialized (DisplaySource, Audio, DisplayOutput)');
    }

    // TODO-STRANGLER-FIG: Bridge loginProfileState to AppCoordinator.authState
    loginProfileState.addListener((state) => {
      appCoordinator.updateAuthState(state.accessToken);
    });
    if (loginProfileState.currentState.accessToken) {
      appCoordinator.updateAuthState(loginProfileState.currentState.accessToken);
    }

    // Android multi-instance host Phase 2: start the capability provider
    // (initial query now; re-query on every Playjector reconnect via the
    // notifyPlayjectorInitialized tap inside the provider), feed the v2 host
    // state report machine section, and flush the report when fresh
    // capabilities land so the fields don't wait for the 30s heartbeat.
    if (USE_PLAYJECTOR_COORDINATOR) {
      instanceCapabilitiesProvider.start();
      gameLaunchCoordinator
        .getGameInstanceReportManager()
        .setCapabilitiesResolver(() => instanceCapabilitiesProvider.capabilities());
      instanceCapabilitiesProvider.addListener(() => {
        gameLaunchCoordinator.getGameInstanceReportManager().flush();
      });
      console.log('[Coordinators] ✓ InstanceCapabilitiesProvider initialized');
    } else {
      console.log('[Coordinators] 🚧 InstanceCapabilitiesProvider skipped (feature flag disabled)');
    }

    // Android multi-instance host Phase 4: route per-instance close
    // notifications into the match lifecycle, arm the restart policy, and wire
    // the capabilities clamp resolver (Phase 3 deviation follow-up — feeds the
    // reported maxGameInstances ceiling into the launch clamp).
    if (USE_PLAYJECTOR_COORDINATOR) {
      serviceContainer.playjectorService.onInstanceClosed(
        createInstanceClosedHandler(() => gameLaunchCoordinator.getMatchLifecycleManager()),
      );
      instanceRecoveryManager.attach();
      gameLaunchCoordinator.getMatchLifecycleManager().setInstanceRecovery(instanceRecoveryManager);
      // Restart-on-player-leave: when the active campaign's restartOnDisconnect
      // is on (default true), an instance whose last player leaves is restarted
      // for a clean state instead of being reused after cooldown.
      gameLaunchCoordinator
        .getMatchLifecycleManager()
        .setRestartOnDisconnectResolver(
          () => hostCampaignConfigState.currentState.restartOnDisconnect !== false,
        );
      // Reject MATCH_ASSIGNED while Pavilion has matchmakeable=false (virtual_display /
      // shadowcam / sanitization gates) so a stale matchmaker cannot seat players.
      gameLaunchCoordinator
        .getMatchLifecycleManager()
        .setHostMatchmakeableResolver(() => pavilionCoordinator.isMatchmakeable());
      gameLaunchCoordinator.setInstanceCapabilitiesResolver(() => instanceCapabilitiesProvider.capabilities());
      // Phase 8 (review fix B1): composite the instanceLaunched echo routing.
      // The GameLaunchCoordinator constructor subscription routes echoes to
      // the launch manager only; this second subscription gives the launch
      // manager first claim and forwards every unclaimed echo to the recovery
      // policy. Without it, a restart's relaunch echo never reaches
      // InstanceRecoveryManager and restarts loop until the budget exhausts.
      serviceContainer.playjectorService.onInstanceLaunched(
        createCompositeInstanceLaunchedHandler(
          () => gameLaunchCoordinator.getGameInstanceLaunchManager(),
          () => instanceRecoveryManager,
        ),
      );
      console.log('[Coordinators] ✓ Instance lifecycle recovery initialized');
    } else {
      console.log('[Coordinators] 🚧 Instance lifecycle recovery skipped (feature flag disabled)');
    }

    console.log('[Coordinators] All coordinators initialized successfully');

    restartManager = initRestartManager(playjectorMessageRouter);
  } catch (error) {
    console.error('[Coordinators] Failed to initialize coordinators:', error);
    throw error;
  }
}

// All coordinator instances are exported at their declaration site above.
